/**
 * An assignment's commercials: what we pay the partner and by when.
 *
 * The deadline is EITHER a fixed due date OR a turnaround — the date
 * is what it is regardless of when the report starts; the turnaround
 * counts from the report's Start press (whoever presses it). The two
 * are mutually exclusive, so the form is a mode switch, not two inputs
 * that mirror each other. Editable after dispatch too: the change
 * syncs to the Backoffice partner request.
 *
 * On an assignment locked to Armadillo i-doc the fee is not the
 * analyst's: the Hub writes it from the registry orders the assignment
 * bills (their total plus any adjustment), and the API refuses a direct
 * edit (409 `registry_fee_managed`). The field is read-only there, with
 * the breakdown the automatic assignment's card shows and "Adjust fee"
 * beside it as the lever.
 */

import { useEffect, useState } from 'react';

import { Badge, Group, NumberInput, SegmentedControl, Stack, Text, TextInput } from '@mantine/core';

import { BackofficeBehindNotice } from '@/components/BackofficeBehindNotice';
import { LastChangedLine } from '@/components/CommercialHistory';
import { ReasonDialog } from '@/components/ReasonDialog';
import { useActorNames } from '@/hooks/useActorNames';
import { latestOf, needsReason } from '@/types/commercial-change';
import {
  describeManagedFee,
  isAssignmentCancelled,
  isLiveManualRegistryAssignment,
  type BackofficeAssignment,
  type BackofficeAssignmentUpdate,
} from '@/types/np-assignments';
import { formatDueDate } from '@/utils';
import { useNpAssignmentsContext } from './NpAssignmentsContext';
import { RegistryFeeAdjustmentControl } from './RegistryFeeAdjustmentModal';
import { registryFeeHint } from './RegistryOrdersBilled';

type DeadlineMode = 'turnaround' | 'due_date';

const DEADLINE_MODES = [
  { value: 'turnaround', label: 'Turnaround' },
  { value: 'due_date', label: 'Due date' },
];

/** Whether the readiness gate would want a fee typed: not once dispatched,
 * and never on an assignment locked to Armadillo, whose fee the Hub keeps
 * from the registry orders. */
function feeIsMissing(
  assignment: BackofficeAssignment,
  fee: number | string,
  dispatched: boolean
): boolean {
  return !dispatched && !isLiveManualRegistryAssignment(assignment) && fee === '';
}

/**
 * The fee input. On an assignment locked to Armadillo and live the fee is
 * the one the registry orders set, shown read-only with pence and how it
 * comes about, and the adjustment beside it is the one way to change it;
 * anywhere else the analyst's own whole-pound figure.
 */
function FeeField({
  assignment,
  fee,
  feeMissing,
  disabled,
  onChange,
  onCommit,
}: Readonly<{
  assignment: BackofficeAssignment;
  fee: number | string;
  feeMissing: boolean;
  disabled: boolean;
  onChange: (fee: number | string) => void;
  onCommit: (fee: number | null) => void;
}>) {
  if (isLiveManualRegistryAssignment(assignment)) {
    return (
      <Stack gap={4}>
        <TextInput
          label="NP fee"
          readOnly
          value={describeManagedFee(assignment)}
          description={registryFeeHint(assignment)}
          data-testid={`registry-fee-${assignment.id}`}
        />
        <Group gap="xs">
          <RegistryFeeAdjustmentControl assignment={assignment} />
          <Text size="xs" c="dimmed">
            Set by the registry orders
          </Text>
        </Group>
      </Stack>
    );
  }
  return (
    <NumberInput
      label="NP fee"
      disabled={disabled}
      placeholder="0"
      min={0}
      allowDecimal={false}
      value={fee}
      error={feeMissing ? 'Fee not set' : undefined}
      onChange={(v) => onChange(typeof v === 'number' ? v : '')}
      onBlur={() => onCommit(fee === '' ? null : Number(fee))}
    />
  );
}

function modeFor(assignment: BackofficeAssignment): DeadlineMode {
  return assignment.npDueDate ? 'due_date' : 'turnaround';
}

interface AssignmentCommercialsFormProps {
  assignment: BackofficeAssignment;
  /** True once the assignment has been sent to Backoffice — the scope
   * and partner are frozen, the commercials are not. */
  dispatched: boolean;
}

export function AssignmentCommercialsForm({
  assignment,
  dispatched,
}: Readonly<AssignmentCommercialsFormProps>) {
  const { actions, reportId, reportCompleted, lastChangeFor, refetchCommercialChanges } =
    useNpAssignmentsContext();
  const nameFor = useActorNames();
  const lastChanges = lastChangeFor(assignment.id);
  // A cancelled assignment is closed: Backoffice refuses fee and deadline changes
  // on a cancelled request, so the figures stay as they were.
  const cancelled = isAssignmentCancelled(assignment);
  // A completed report's figures may already have been billed, so a change to
  // them waits here for a reason before it is sent.
  const [pending, setPending] = useState<{
    patch: BackofficeAssignmentUpdate;
    fields: string[];
  } | null>(null);
  const [saving, setSaving] = useState(false);
  const [fee, setFee] = useState<number | string>(assignment.npFee ?? '');
  const [tat, setTat] = useState<number | string>(assignment.turnaroundTimeDays ?? '');
  // Native date input value is a YYYY-MM-DD string (matches the wire shape).
  const [due, setDue] = useState<string>(assignment.npDueDate ?? '');
  const [mode, setMode] = useState<DeadlineMode>(modeFor(assignment));

  // Re-sync local inputs when the server value changes (e.g. after lock).
  useEffect(() => {
    setFee(assignment.npFee ?? '');
  }, [assignment.npFee]);
  useEffect(() => {
    setTat(assignment.turnaroundTimeDays ?? '');
  }, [assignment.turnaroundTimeDays]);
  useEffect(() => {
    setDue(assignment.npDueDate ?? '');
  }, [assignment.npDueDate]);
  useEffect(() => {
    setMode(modeFor(assignment));
  }, [assignment.npDueDate, assignment.turnaroundTimeDays]);

  const locked = assignment.status === 'np_locked';
  let syncNote = '(sent to Backoffice when dispatched)';
  if (cancelled) {
    syncNote = '(cancelled — kept on record)';
  } else if (dispatched) {
    syncNote = '(syncs to Backoffice)';
  }
  // The readiness gate hard-requires a fee and (turnaround OR due date)
  // before the report can be marked Ready — surface that up front rather
  // than letting analysts discover it at the Ready click. A fee the Hub
  // keeps from the registry orders is never missing (`feeIsMissing`).
  const feeMissing = feeIsMissing(assignment, fee, dispatched);
  const deadlineMissing = !dispatched && tat === '' && due === '';

  const save = async (patch: BackofficeAssignmentUpdate, reason?: string) => {
    await actions.update(assignment.id, reason ? { ...patch, reason } : patch);
    void refetchCommercialChanges();
  };

  /** Sends an edit — or, on a completed report, asks why first. Before
   * completion every edit goes straight through, as it always did. */
  const submit = (patch: BackofficeAssignmentUpdate, fields: string[], changed: boolean) => {
    if (!reportCompleted) {
      void save(patch);
      return;
    }
    if (!changed) {
      return;
    }
    if (needsReason(fields)) {
      setPending({ patch, fields });
      return;
    }
    void save(patch);
  };

  /** Back to what the server holds — a cancelled edit must not stay on screen. */
  const resetInputs = () => {
    setFee(assignment.npFee ?? '');
    setTat(assignment.turnaroundTimeDays ?? '');
    setDue(assignment.npDueDate ?? '');
    setMode(modeFor(assignment));
  };

  const switchMode = (next: DeadlineMode) => {
    setMode(next);
    // Leaving a mode clears its value — the two are exclusive.
    if (next === 'turnaround' && assignment.npDueDate) {
      setDue('');
      submit({ npDueDate: null }, ['np_due_date'], true);
    }
    if (next === 'due_date' && assignment.turnaroundTimeDays != null) {
      setTat('');
      submit({ turnaroundTimeDays: null }, ['np_turnaround_time_days'], true);
    }
  };

  return (
    <Stack gap={4} data-testid={`assignment-commercials-${assignment.id}`}>
      <Group gap="xs">
        <Text size="xs" c="dimmed" fw={500}>
          NP fee &amp; deadline
        </Text>
        {(feeMissing || deadlineMissing) && (
          <Badge size="xs" color="yellow" variant="light">
            Required before Ready
          </Badge>
        )}
        {locked && (
          <Text size="xs" c="orange">
            (editing a locked assignment)
          </Text>
        )}
        <Text size="xs" c="dimmed">
          {syncNote}
        </Text>
        <BackofficeBehindNotice
          marker={assignment.backofficeSyncMarker}
          what="partner fee and deadline"
        />
      </Group>
      <Group grow align="flex-start">
        <FeeField
          assignment={assignment}
          fee={fee}
          feeMissing={feeMissing}
          disabled={cancelled}
          onChange={setFee}
          onCommit={(next) => submit({ npFee: next }, ['np_fee'], next !== assignment.npFee)}
        />
        <Stack gap={2}>
          <Text size="sm" fw={500}>
            Deadline
          </Text>
          <SegmentedControl
            size="xs"
            data={DEADLINE_MODES}
            disabled={cancelled}
            value={mode}
            onChange={(v) => switchMode(v as DeadlineMode)}
            data-testid={`assignment-deadline-mode-${assignment.id}`}
          />
        </Stack>
      </Group>
      {mode === 'turnaround' ? (
        <NumberInput
          label="Turnaround (days from Start)"
          disabled={cancelled}
          placeholder="—"
          min={0}
          allowDecimal={false}
          value={tat}
          error={deadlineMissing ? 'Set a turnaround or a due date' : undefined}
          description={
            assignment.turnaroundTimeDays != null && assignment.effectiveNpDueDate
              ? `Due ${formatDueDate(assignment.effectiveNpDueDate)}${
                  dispatched ? '' : ' if started today'
                }`
              : 'Counts from the report Start, whoever presses it'
          }
          onChange={(v) => setTat(typeof v === 'number' ? v : '')}
          onBlur={() => {
            const next = tat === '' ? null : Number(tat);
            submit(
              { turnaroundTimeDays: next },
              ['np_turnaround_time_days'],
              next !== assignment.turnaroundTimeDays
            );
          }}
        />
      ) : (
        <TextInput
          type="date"
          label="Due date (fixed)"
          disabled={cancelled}
          value={due}
          error={deadlineMissing ? 'Set a turnaround or a due date' : undefined}
          description="Stands regardless of when the report starts"
          onChange={(e) => {
            const next = e.currentTarget.value;
            setDue(next);
            submit(
              { npDueDate: next || null },
              ['np_due_date'],
              (next || null) !== assignment.npDueDate
            );
          }}
        />
      )}
      <LastChangedLine label="Fee" change={lastChanges.np_fee} nameFor={nameFor} />
      <LastChangedLine
        label="Deadline"
        change={latestOf(lastChanges.np_due_date, lastChanges.np_turnaround_time_days)}
        nameFor={nameFor}
      />
      <ReasonDialog
        opened={pending !== null}
        reportId={reportId}
        changedFields={pending?.fields ?? []}
        assignmentId={assignment.id}
        submitting={saving}
        onConfirm={async (reason) => {
          if (!pending) {
            return;
          }
          setSaving(true);
          try {
            await save(pending.patch, reason);
          } finally {
            setSaving(false);
            setPending(null);
          }
        }}
        onCancel={() => {
          setPending(null);
          resetInputs();
        }}
      />
    </Stack>
  );
}
